import { LazyQueryHookOptions, QueryHookOptions } from '@apollo/client';
import _ from 'lodash';

import {
  OfferFragment,
  ProductGroupFilter,
  ProductGroupFragment,
  ProductGroupsQuery,
  ProductGroupsQueryVariables,
  useProductGroupQuery,
  useProductGroupsLazyQuery,
  useProductGroupsQuery,
} from '@occc/graphql';
import { PaginationInfo, usePaginatedQuery } from '@occc-app/hooks/usePaginatedQuery';

import { getProductGroupIdsToLoad } from './utils';

import { useLazyQuery } from '../utils';

interface GetProductGroupsProps {
  filter: ProductGroupFilter;
  pageSize?: number;
  baseOptions?: QueryHookOptions<ProductGroupFragment[], ProductGroupsQueryVariables>;
  paginationInfo?: PaginationInfo;
  onPageChange?: (info: PaginationInfo) => void;
}

const transformDataToProductGroupsArray = (data: ProductGroupsQuery): ProductGroupFragment[] => {
  const { edges } = data.productGroups;
  return _.map(edges, 'node');
};

export const useGetProductGroups = ({
  filter,
  pageSize,
  baseOptions,
  paginationInfo,
  onPageChange,
}: GetProductGroupsProps) => {
  const response = usePaginatedQuery(
    useProductGroupsQuery,
    'productGroups.pageInfo',
    pageSize,
    {
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
      variables: {
        filter,
      },
      ...baseOptions,
      onCompleted: (data) => {
        if (baseOptions && baseOptions.onCompleted) {
          baseOptions.onCompleted(transformDataToProductGroupsArray(data));
        }
      },
    } as QueryHookOptions,
    paginationInfo,
    onPageChange,
  );

  return {
    ...response,
    data: response.data ? transformDataToProductGroupsArray(response.data) : response.data,
  };
};

export const useGetProductGroupsLazy = (
  baseOptions?: LazyQueryHookOptions<ProductGroupsQuery, ProductGroupsQueryVariables>,
) => {
  const [executeQuery, response] = useLazyQuery(useProductGroupsLazyQuery, baseOptions);

  return {
    executeQuery,
    response: {
      ...response,
      data: response.data ? transformDataToProductGroupsArray(response.data) : response.data,
    },
  };
};

export const useGetProductGroupById = (
  idEquals: string | null | undefined,
  chainIdEquals?: string,
) => {
  const response = useProductGroupQuery({
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    skip: !idEquals,
    variables: {
      filter: {
        idEquals,
        ...(chainIdEquals ? { chainIdEquals } : {}),
        isDeleted: false,
      },
    },
  });

  return {
    ...response,
    data: response.data?.productGroups.edges?.[0]?.node,
  };
};

export const useGetProductGroupsForOffer = (offer?: OfferFragment) => {
  const productGroupIdsToLoad = offer ? getProductGroupIdsToLoad(offer.constraints) : [];

  const { data: productGroupsResponse } = useProductGroupsQuery({
    variables: {
      filter: {
        idContains: productGroupIdsToLoad,
      },
    },
    skip: productGroupIdsToLoad.length === 0,
  });

  const productGroups = productGroupsResponse?.productGroups.edges.map((edge) => edge.node);

  return productGroups || [];
};

export const useGetProductGroupsByIds = (idContains: string[]) => {
  const response = useProductGroupsQuery({
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    skip: !idContains.length,
    variables: {
      filter: {
        idContains,
      },
    },
  });

  return {
    ...response,
    data: response.data
      ? response.data.productGroups.edges.map((edge) => edge.node)
      : response.data,
  };
};
